/**
 * =============================================================================
 * Deenex AI - App Component
 * =============================================================================
 *
 * Root component that sets up routing and global providers.
 *
 * @module App
 */

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AppProvider } from "./context/AppContext";
import { AccessPage } from "./pages/Access/AccessPage";
import { ProtectedRoute } from "./components/layout/ProtectedRoute";
import { SoloActiva } from "./components/layout/SoloActiva";
import { HOME } from "./config/producto";
import DashboardLayout from "./layouts/DashboardLayout";
import { useAuth } from "./hooks/useAuth";
import { Toaster } from "./shared/components/ui/Toaster";

// Module Pages
import DashboardPage from "./modules/dashboard/pages/DashboardPage";
import {
  ObjectivesListPage,
  ObjectiveCreatePage,
  ObjectiveCreateCustomPage,
  ObjectiveSetupPage,
  ObjectiveManualWizardPage,
  ObjectiveAutoWizardPage,
  ObjectiveDashboardPage,
} from "./modules/objectives";
import AudiencePage from "./modules/audience/pages/AudiencePage";
import BrandPage from "./modules/brand/pages/BrandPage";
import WalletPage from "./modules/wallet/pages/WalletPage";
import IntegrationsPage from "./modules/integrations/pages/IntegrationsPage";
import { SettingsPage } from "./modules/settings/pages/SettingsPage";
import {
  SegmentsPage,
  SegmentCreatePage,
  SegmentCreateAIPage,
  SegmentCreateManualPage,
  SegmentDashboardPage,
} from "./modules/segments";
import {
  NotificationsPage,
  NotificationsAnalyticsPage,
} from "./modules/notifications";
import {
  WhatsAppCampaignsPage,
  WhatsAppAnalyticsPage,
  WhatsAppTemplatesPage,
  WhatsAppTemplateEditorPage,
  WhatsAppAccountsPage,
  WhatsAppAccountSetupPage,
} from "./modules/whatsapp";
import {
  CampanaNuevaListPage,
  CampanaDetallePage,
  ReportePublicoPage,
  BajaPublicaPage,
  ClientePortalPage,
  CampanaNuevaRealPage,
  WhatsAppConexionPage,
  EmailCuentasPage,
} from "./modules/campana-nueva";
import {
  OficialLayout,
  ResumenOficialPage,
  PlantillasListaPage,
  PlantillasModelosPage,
  PlantillaEditorPage,
  PlantillaDetallePage,
  CampanasOficialListaPage,
  CampanaAsistentePage,
  CampanaPlantillaPage,
  CampanaOficialDetallePage,
  BajasConsentimientosPage,
  ConfiguracionOficialPage,
  EstadisticasOficialPage,
  CalentamientoPage,
  ImagenesPage,
} from "./modules/wa-oficial";
// «Comparar campañas» del oficial: su archivo, como las de la Base (el barrel lo tocan otros frentes).
import ComparativaCampanasPage from "./modules/wa-oficial/pages/ComparativaCampanasPage";
// «Hacer un seguimiento» de una campaña del oficial (28/10/2026): también por su archivo.
import CampanaSeguimientoPage from "./modules/wa-oficial/pages/CampanaSeguimientoPage";
// «Repetir esta campaña» del oficial (28/10/2026): también por su archivo.
import CampanaRepetirPage from "./modules/wa-oficial/pages/CampanaRepetirPage";
// Las listas de exclusión del oficial (28/10/2026): también por su archivo.
import ListasExclusionPage from "./modules/wa-oficial/pages/ListasExclusionPage";
// Los disparadores del oficial (29/10/2026): también por su archivo.
import DisparadoresPage from "./modules/wa-oficial/pages/DisparadoresPage";
// Base de datos del WhatsApp oficial (SPEC-BD §6.1): cada página por su archivo.
import BaseLayout from "./modules/wa-oficial/pages/base/BaseLayout";
import CargaNuevaPage from "./modules/wa-oficial/pages/base/CargaNuevaPage";
import CargaDetallePage from "./modules/wa-oficial/pages/base/CargaDetallePage";
import ContactosPage from "./modules/wa-oficial/pages/base/ContactosPage";
import ContactoFichaPage from "./modules/wa-oficial/pages/base/ContactoFichaPage";
import PublicosPage from "./modules/wa-oficial/pages/base/PublicosPage";
import PublicoEditorPage from "./modules/wa-oficial/pages/base/PublicoEditorPage";
import { R_CARGAS_LISTA } from "./modules/wa-oficial/components/base/comun";
import CargasLayout, { HaciaLasCargas } from "./modules/publico/pages/CargasLayout";
// Conversaciones del WhatsApp oficial: también archivo por archivo. El barrel lo mockea
// `App.routes.test.tsx` con una lista cerrada, y un export nuevo ahí tiraba todos los tests de rutas.
import ConversacionesLayout from "./modules/wa-oficial/pages/conversaciones/ConversacionesLayout";
import ConversacionesIndexPage from "./modules/wa-oficial/pages/conversaciones/ConversacionesIndexPage";
import ConversacionPage from "./modules/wa-oficial/pages/conversaciones/ConversacionPage";
// Accesos (quién puede entrar por mail): por su archivo, igual que la base y Conversaciones.
import AccesosPage from "./modules/accesos/AccesosPage";
// Emails (Snov): también página por página.
import EmailsLayout from "./modules/emails/pages/EmailsLayout";
import ResumenEmailsPage from "./modules/emails/pages/ResumenEmailsPage";
import AccionesEmailsPage from "./modules/emails/pages/AccionesEmailsPage";
import CampanasEmailsPage from "./modules/emails/pages/CampanasEmailsPage";
import CampanaEmailPage from "./modules/emails/pages/CampanaEmailPage";
import PersonasEmailsPage from "./modules/emails/pages/PersonasEmailsPage";
import PersonaEmailPage from "./modules/emails/pages/PersonaEmailPage";
import RespuestasEmailsPage from "./modules/emails/pages/RespuestasEmailsPage";
import ConexionSnovPage from "./modules/emails/pages/ConexionSnovPage";
import PublicoPage from "./modules/publico/pages/PublicoPage";
import PersonaPublicoPage from "./modules/publico/pages/PersonaPublicoPage";
import SubirBasePage from "./modules/base-ventas/pages/SubirBasePage";
import InicioPage from "./modules/inicio/pages/InicioPage";
// «Campañas», la lista única de los cuatro mundos (28/10/2026); la de arriba es la de las campañas con segmentación.
import TodasLasCampanasPage from "./modules/campanas/pages/CampanasPage";
import NuevaCampanaPage from "./modules/campanas/pages/NuevaCampanaPage";
import CalendarioCampanasPage from "./modules/campanas/pages/CalendarioCampanasPage";
import CampanasPage from "./modules/segmentaciones/pages/CampanasPage";
import SegmentarPage from "./modules/segmentaciones/pages/SegmentarPage";
import DesdePublicoPage from "./modules/segmentaciones/pages/DesdePublicoPage";
import GruposPage from "./modules/segmentaciones/pages/GruposPage";
import DescuentosPage from "./modules/descuentos/pages/DescuentosPage";
import BrandingPage from "./modules/branding/pages/BrandingPage";
import CuponesPage from "./modules/cupones/pages/CuponesPage";
import MensajesPage from "./modules/mensajes/pages/MensajesPage";
import ResultadoPage from "./modules/envios/pages/ResultadoPage";
import EstadisticasPage from "./modules/envios/pages/EstadisticasPage";
import { PaginaSoloEquipo } from "./modules/campana-nueva/components/SoloEquipo";

/**
 * Audit G-01: ruta comodín. Un usuario logueado que tipea mal una URL o sigue
 * un deep-link viejo NO debe caer en /login (parece "logout sorpresa"): lo
 * mandamos al inicio (HOME, config/producto). Los no autenticados sí van a login.
 */
function NotFoundRedirect() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  return <Navigate to={isAuthenticated ? HOME : "/login"} replace />;
}

/**
 * Las rutas de la app, sin el router: App las envuelve en BrowserRouter y los tests en un
 * MemoryRouter, así el ruteo real (redirects, comodín, deep-link) se prueba sin navegador.
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<AccessPage />} />
      {/* Reporte comercial compartible por link (sin login) */}
      <Route path="/r/:token" element={<ReportePublicoPage />} />
      {/* Baja desde el link del mail: la abre el cliente final, que no tiene cuenta */}
      <Route path="/baja/:token" element={<BajaPublicaPage />} />
      {/* Portal de consulta del cliente (cuenta del negocio) */}
      <Route path="/cliente" element={<ClientePortalPage />} />

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        {/* Dashboard Layout wraps all these routes */}
        <Route element={<DashboardLayout />}>
          <Route path="/activa" element={<CampanaNuevaListPage />} />
          {/* Campañas: todas las del comercio en una lista (WhatsApp oficial, con segmentación, por QR y mail), con el
              link al detalle de cada una. La ven el equipo y el cliente (al cliente, sin las con segmentación). Las listas
              de cada mundo siguen en sus rutas. */}
          <Route path="/activa/campanas" element={<TodasLasCampanasPage />} />
          {/* Elegir qué campaña armar: adonde llevan los dos «Nueva campaña» (el del menú y el de la lista). Del equipo, como
              cada asistente. Estática: le gana a /activa/campanas/:id. */}
          <Route path="/activa/campanas/nueva" element={<PaginaSoloEquipo volverA="/activa/campanas"><NuevaCampanaPage /></PaginaSoloEquipo>} />
          {/* El calendario de esas campañas, con la fatiga a la vista (fase 3). Lo ven los mismos que la lista. Estática: le
              gana a /activa/campanas/:id. */}
          <Route path="/activa/campanas/calendario" element={<CalendarioCampanasPage />} />
          <Route path="/activa/campanas/:id" element={<CampanaDetallePage />} />
          {/* Lo que arma y conecta el equipo de Deenex: una persona de un cliente (Accesos, rol
              «comercio») ve un aviso en vez de la página (PaginaSoloEquipo); el backend igual se lo niega. */}
          <Route path="/activa/real" element={<PaginaSoloEquipo volverA="/activa"><CampanaNuevaRealPage /></PaginaSoloEquipo>} />
          <Route path="/activa/whatsapp" element={<PaginaSoloEquipo volverA="/activa"><WhatsAppConexionPage /></PaginaSoloEquipo>} />
          <Route path="/activa/email" element={<PaginaSoloEquipo volverA="/activa"><EmailCuentasPage /></PaginaSoloEquipo>} />
          {/* Una sola lista de bajas para toda la marca: vive en WhatsApp → Bajas (que la embebe junto con las
              constancias y los consentimientos). Antes había dos entradas en el menú para la misma lista. */}
          <Route path="/activa/no-contactar" element={<Navigate to="/activa/oficial/bajas" replace />} />
          <Route path="/activa/accesos" element={<PaginaSoloEquipo volverA="/activa"><AccesosPage /></PaginaSoloEquipo>} />
          {/* WhatsApp oficial (API de Meta vía Twilio). Adentro de DashboardLayout y FUERA de
              SoloActiva a propósito: es parte de Activa, y empezar con /activa le hace heredar
              `esActiva` (sin la burbuja del assistant). */}
          <Route path="/activa/oficial" element={<OficialLayout />}>
            <Route index element={<ResumenOficialPage />} />
            <Route path="plantillas" element={<PlantillasListaPage />} />
            <Route path="plantillas/nueva" element={<PaginaSoloEquipo><PlantillaEditorPage /></PaginaSoloEquipo>} />
            <Route path="plantillas/modelos" element={<PaginaSoloEquipo><PlantillasModelosPage /></PaginaSoloEquipo>} />
            <Route path="plantillas/:id" element={<PlantillaDetallePage />} />
            <Route path="plantillas/:id/editar" element={<PaginaSoloEquipo><PlantillaEditorPage /></PaginaSoloEquipo>} />
            <Route path="campanas" element={<CampanasOficialListaPage />} />
            <Route path="campanas/nueva" element={<PaginaSoloEquipo><CampanaAsistentePage /></PaginaSoloEquipo>} />
            {/* la ven el equipo y el cliente, como la lista; estática, antes de campanas/:id */}
            <Route path="campanas/comparar" element={<ComparativaCampanasPage />} />
            <Route path="campanas/:id" element={<CampanaOficialDetallePage />} />
            <Route path="campanas/:id/editar" element={<PaginaSoloEquipo><CampanaAsistentePage /></PaginaSoloEquipo>} />
            <Route path="campanas/:id/plantilla" element={<PaginaSoloEquipo><CampanaPlantillaPage /></PaginaSoloEquipo>} />
            {/* a una parte de los que la recibieron: elegir el criterio y armar la hija (del equipo, como armar una campaña) */}
            <Route path="campanas/:id/hacer-seguimiento" element={<PaginaSoloEquipo><CampanaSeguimientoPage /></PaginaSoloEquipo>} />
            {/* cada semana, cada 2 o cada mes, hasta una fecha o N veces (del equipo, como armar una campaña) */}
            <Route path="campanas/:id/repetir" element={<PaginaSoloEquipo><CampanaRepetirPage /></PaginaSoloEquipo>} />
            {/* El `:id` es el de la conversación, nunca el teléfono. */}
            <Route path="conversaciones" element={<ConversacionesLayout />}>
              <Route index element={<ConversacionesIndexPage />} />
              <Route path=":id" element={<ConversacionPage />} />
            </Route>
            <Route path="estadisticas" element={<EstadisticasOficialPage />} />
            <Route path="calentamiento" element={<CalentamientoPage />} />
            {/* fase 2: las imágenes de las plantillas (subir y borrar son del equipo; un cliente sólo ve la lista) */}
            <Route path="imagenes" element={<ImagenesPage />} />
            {/* las listas de exclusión de la marca (28/10/2026, del equipo: la página misma le avisa a un cliente) */}
            <Route path="exclusiones" element={<ListasExclusionPage />} />
            {/* las reglas que arman un borrador a su hora (29/10/2026, del equipo, como el asistente) */}
            <Route path="disparadores" element={<PaginaSoloEquipo><DisparadoresPage /></PaginaSoloEquipo>} />
            {/* «Base de datos» salió del menú el 17/10/2026: su entrada y sus cargas son de Público, y las direcciones
                viejas llevan ahí. Quedan los contactos y los públicos, que usa «Un público de tu base». */}
            <Route path="base" element={<BaseLayout />}>
              <Route index element={<Navigate to="/activa/publico" replace />} />
              <Route path="cargas" element={<Navigate to={R_CARGAS_LISTA} replace />} />
              <Route path="cargas/nueva" element={<HaciaLasCargas />} />
              <Route path="cargas/:id" element={<HaciaLasCargas />} />
              <Route path="contactos" element={<ContactosPage />} />
              <Route path="contactos/:id" element={<ContactoFichaPage />} />
              <Route path="publicos" element={<PublicosPage />} />
              <Route path="publicos/nuevo" element={<PaginaSoloEquipo><PublicoEditorPage /></PaginaSoloEquipo>} />
              <Route path="publicos/:id" element={<PaginaSoloEquipo><PublicoEditorPage /></PaginaSoloEquipo>} />
            </Route>
            <Route path="bajas" element={<BajasConsentimientosPage />} />
            <Route path="configuracion" element={<ConfiguracionOficialPage />} />
          </Route>
          {/* Inicio de DEENEX Activa (DISENO §1): del equipo, como el menú nuevo hasta que esté completo. El inicio de la app
              (HOME) sigue siendo el de WhatsApp hasta entonces. */}
          <Route path="/activa/inicio" element={<PaginaSoloEquipo><InicioPage /></PaginaSoloEquipo>} />
          {/* Campañas nuevas (DEENEX Activa, DISENO §5, §6 y §9): cada segmentación nace como una campaña en borrador. Del
              equipo. Las campañas de Reactivación siguen en /activa/campanas. */}
          <Route path="/activa/segmentaciones" element={<PaginaSoloEquipo><CampanasPage /></PaginaSoloEquipo>} />
          <Route path="/activa/segmentaciones/nueva" element={<PaginaSoloEquipo><SegmentarPage /></PaginaSoloEquipo>} />
          {/* «Con los grupos de tu Público» (01/11/2026): los grupos que ya tiene cada persona, sin segmentar de nuevo */}
          <Route path="/activa/segmentaciones/desde-publico" element={<PaginaSoloEquipo><DesdePublicoPage /></PaginaSoloEquipo>} />
          <Route path="/activa/segmentaciones/:id" element={<PaginaSoloEquipo><SegmentarPage /></PaginaSoloEquipo>} />
          <Route path="/activa/segmentaciones/:id/grupos" element={<PaginaSoloEquipo><GruposPage /></PaginaSoloEquipo>} />
          <Route path="/activa/segmentaciones/:id/mensajes" element={<PaginaSoloEquipo><MensajesPage /></PaginaSoloEquipo>} />
        <Route path="/activa/segmentaciones/:id/resultado" element={<PaginaSoloEquipo><ResultadoPage /></PaginaSoloEquipo>} />
        <Route path="/activa/estadisticas" element={<PaginaSoloEquipo><EstadisticasPage /></PaginaSoloEquipo>} />
          {/* Tu marca (fase 3): la identidad de la marca, y las reglas de descuento y la medición. Del equipo. */}
          <Route path="/activa/branding" element={<PaginaSoloEquipo><BrandingPage /></PaginaSoloEquipo>} />
          <Route path="/activa/descuentos" element={<PaginaSoloEquipo><DescuentosPage /></PaginaSoloEquipo>} />
          <Route path="/activa/cupones" element={<PaginaSoloEquipo><CuponesPage /></PaginaSoloEquipo>} />
          {/* Público: una fila por persona juntando la Base de WhatsApp, Snov y Reactivación (pedido de Alan del 12/10). */}
          <Route path="/activa/publico" element={<PublicoPage />} />
          {/* Subir la base de ventas (DEENEX Activa, DISENO §3): del equipo. Va antes de la ficha por claridad; igual
              gana por ser un segmento fijo. */}
          <Route path="/activa/publico/subir" element={<PaginaSoloEquipo volverA="/activa/publico"><SubirBasePage /></PaginaSoloEquipo>} />
          {/* Las cargas con consentimiento del oficial (antes en la Base): la lista es la pestaña «Con consentimiento». */}
          <Route path="/activa/publico/cargas" element={<CargasLayout />}>
            <Route index element={<Navigate to={R_CARGAS_LISTA} replace />} />
            <Route path="nueva" element={<PaginaSoloEquipo volverA={R_CARGAS_LISTA}><CargaNuevaPage /></PaginaSoloEquipo>} />
            <Route path=":id" element={<CargaDetallePage />} />
          </Route>
          <Route path="/activa/publico/:clave" element={<PersonaPublicoPage />} />
          {/* Emails (Snov): las estadísticas, la base de personas y las respuestas de las campañas de mail del comercio.
              «Conexión» (las casillas de la cuenta de Snov y de qué comercio es cada campaña) es del equipo. */}
          <Route path="/activa/emails" element={<EmailsLayout />}>
            <Route index element={<ResumenEmailsPage />} />
            <Route path="acciones" element={<AccionesEmailsPage />} />
            <Route path="campanas" element={<CampanasEmailsPage />} />
            <Route path="campanas/:id" element={<CampanaEmailPage />} />
            <Route path="personas" element={<PersonasEmailsPage />} />
            <Route path="personas/:id" element={<PersonaEmailPage />} />
            <Route path="respuestas" element={<RespuestasEmailsPage />} />
            <Route path="conexion" element={<PaginaSoloEquipo volverA="/activa/emails"><ConexionSnovPage /></PaginaSoloEquipo>} />
          </Route>

          {/* Plataforma legacy: con SOLO_ACTIVA todas redirigen al inicio (HOME, config/producto). */}
          <Route element={<SoloActiva />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/objectives" element={<ObjectivesListPage />} />
            <Route path="/objectives/new" element={<ObjectiveCreatePage />} />
            <Route path="/objectives/new/custom" element={<ObjectiveCreateCustomPage />} />
            <Route path="/objectives/:id/setup" element={<ObjectiveSetupPage />} />
            <Route path="/objectives/:id/setup/manual" element={<ObjectiveManualWizardPage />} />
            <Route path="/objectives/:id/setup/auto" element={<ObjectiveAutoWizardPage />} />
            <Route path="/objectives/:id" element={<ObjectiveDashboardPage />} />
            <Route path="/audience" element={<AudiencePage />} />
            <Route path="/segments" element={<SegmentsPage />} />
            <Route path="/segments/new" element={<SegmentCreatePage />} />
            <Route path="/segments/new/ai" element={<SegmentCreateAIPage />} />
            <Route path="/segments/new/manual" element={<SegmentCreateManualPage />} />
            <Route path="/segments/:id" element={<SegmentDashboardPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/notifications/analytics" element={<NotificationsAnalyticsPage />} />
            <Route path="/whatsapp" element={<WhatsAppCampaignsPage />} />
            <Route path="/whatsapp/accounts" element={<WhatsAppAccountsPage />} />
            <Route path="/whatsapp/accounts/:id/setup" element={<WhatsAppAccountSetupPage />} />
            <Route path="/whatsapp/analytics" element={<WhatsAppAnalyticsPage />} />
            <Route path="/whatsapp/templates" element={<WhatsAppTemplatesPage />} />
            <Route path="/whatsapp/templates/new" element={<WhatsAppTemplateEditorPage />} />
            <Route path="/whatsapp/templates/:id" element={<WhatsAppTemplateEditorPage />} />
            <Route path="/brand" element={<BrandPage />} />

            {/* Maquetas de la plataforma anterior (datos escritos a mano): con SOLO_ACTIVA
                también redirigen al inicio (HOME). */}
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* «/» va al inicio (HOME, config/producto): /activa/oficial con SOLO_ACTIVA. */}
          <Route
            path="/"
            element={<Navigate to={HOME} replace />}
          />
        </Route>
      </Route>

      {/* 404 — logueados al inicio, anónimos al login (Audit G-01) */}
      <Route path="*" element={<NotFoundRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <AppRoutes />
          {/* Un solo Toaster para toda la app, también fuera del layout: /login avisa por toast
              el reenvío del código. Montarlo en otro lado además lo duplicaría. */}
          <Toaster />
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
